import { isSuccess } from '@/context/services/type';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import type { PostAgreementAiServiceMutationError } from 'ap-openapi';
import {
  useGetApOfficeAiSettingSuspense,
  usePostAgreementAiService,
} from 'ap-openapi';
import type { AxiosResponse } from 'axios';
import { useCallback, useMemo } from 'react';
import type { AiService, AiServiceSettingResponse } from './type';

export type { AiServiceSettingResponse } from './type';

export const useGetAiServiceSetting = (): AiServiceSettingResponse => {
  const { data } = useGetApOfficeAiSettingSuspense();

  return useMemo(() => {
    const {
      ai_service_settings,
      is_ai_service_agreed,
      is_model_training_allowed,
    } = data.data;

    const services: AiService[] = ai_service_settings.map(
      ({ description, name, service_type }) => ({
        description,
        name,
        service_type,
      })
    );

    return {
      ai_service_settings: services,
      is_ai_service_agreed,
      is_model_training_allowed,
    };
  }, [data]);
};

export const useAgreeAiService = () => {
  const { mutateAsync: _mutateAsync, isPending } = usePostAgreementAiService();
  const { t } = useTranslation();

  const mutateAsync = useCallback(
    async (isModelTrainingAllowed: boolean) => {
      let response: AxiosResponse<void | PostAgreementAiServiceMutationError>;

      try {
        response = await _mutateAsync({
          data: {
            is_model_training_allowed: isModelTrainingAllowed,
          },
        });
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }

      if (isSuccess(response.status)) {
        return { isSuccessful: true } as const;
      }

      if (!response.data || !isCommonError(response.data)) {
        throw new Error(
          `Unexpected AI service agreement response shape: ${response.status}`
        );
      }

      const { messages } = response.data;

      return {
        isSuccessful: false,
        message: messages?.length
          ? messages.join(', ')
          : t('ai_service_setting.agreement_error'),
      } as const;
    },
    [_mutateAsync, t]
  );

  return { isPending, mutateAsync };
};
